import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Spinner } from '@/components/ui/spinner'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { useAuthStore, type Post, type User } from '@/stores/auth-store'

type HandoffPayload = {
  user: User
  accessToken: string
  refreshToken: string
  posts?: Post[]
  sessionId?: string
  serviceInstanceId?: string
}

// Reached by a top-level navigation from the marketing site right after a
// real signup there (see ellantec-company-site's organizationSignup.ts).
// The token bundle travels in the URL fragment -- fragments are never sent
// to a server or written to access logs, unlike a query string -- and is
// cleared from the address bar immediately after being read.
export default function AuthHandoffPage() {
  const navigate = useNavigate()
  const hasRunRef = useRef(false)

  useEffect(() => {
    if (hasRunRef.current) {
      return
    }
    hasRunRef.current = true

    const hash = window.location.hash.replace(/^#/, '')
    window.history.replaceState(null, '', window.location.pathname + window.location.search)

    if (!hash) {
      navigate('/login', { replace: true })
      return
    }

    try {
      const decoded = JSON.parse(decodeURIComponent(atob(hash))) as Partial<HandoffPayload>
      if (!decoded.user || !decoded.accessToken || !decoded.refreshToken) {
        throw new Error('Incomplete handoff payload')
      }

      useAuthStore.getState().login(
        decoded.user,
        decoded.accessToken,
        decoded.refreshToken,
        decoded.posts || [],
        decoded.sessionId,
        decoded.serviceInstanceId,
      )

      navigate(buildPostSelectionPath(decoded.serviceInstanceId, null), { replace: true })
    } catch {
      toast.error('Could not complete sign-in. Please sign in manually.')
      navigate('/login', { replace: true })
    }
  }, [navigate])

  return (
    <div className="flex min-h-screen items-center justify-center gap-3 text-muted-foreground">
      <Spinner />
      <span>Setting up your account...</span>
    </div>
  )
}
